import React from 'react';

import { injectIntl, type WrappedComponentProps } from 'react-intl-next';

import type { DispatchAnalyticsEvent } from '@atlaskit/editor-common/analytics';
import {
	sharedPluginStateHookMigratorFactory,
	useSharedPluginStateWithSelector,
} from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { CompanyHubTextColorPlugin } from '../../../common/types/companyHubTextColorPlugin';

import { ToolbarCompanyHubTextColor, ToolbarType } from './ToolbarCompanyHubTextColor';

export interface Props {
	editorView: EditorView;
	isReducedSpacing?: boolean;
	popupsMountPoint?: HTMLElement;
	popupsBoundariesElement?: HTMLElement;
	popupsScrollableElement?: HTMLElement;
	dispatchAnalyticsEvent?: DispatchAnalyticsEvent;
	disabled?: boolean;
	api: ExtractInjectionAPI<CompanyHubTextColorPlugin> | undefined;
}

const useSharedState = sharedPluginStateHookMigratorFactory(
	(api: ExtractInjectionAPI<CompanyHubTextColorPlugin> | undefined) => {
		const { color, defaultColor, disabled } = useSharedPluginStateWithSelector(
			api,
			['companyHubTextColor'],
			(states) => ({
				color: states.companyHubTextColorState?.color,
				defaultColor: states.companyHubTextColorState?.defaultColor,
				disabled: states.companyHubTextColorState?.disabled,
			}),
		);
		return {
			color,
			defaultColor,
			textColorDisabled: disabled,
		};
	},
	(api: ExtractInjectionAPI<CompanyHubTextColorPlugin> | undefined) => {
		const { color, defaultColor, disabled } = useSharedPluginStateWithSelector(
			api,
			['companyHubTextColor'],
			(states) => ({
				color: states.companyHubTextColorState?.color,
				defaultColor: states.companyHubTextColorState?.defaultColor,
				disabled: states.companyHubTextColorState?.disabled,
			}),
		);
		return {
			color,
			defaultColor,
			textColorDisabled: disabled,
		};
	},
);

export const PrimaryToolbarComponent = injectIntl(
	({
		editorView,
		popupsMountPoint,
		popupsBoundariesElement,
		popupsScrollableElement,
		isReducedSpacing,
		dispatchAnalyticsEvent,
		disabled,
		api,
	}: Props & WrappedComponentProps) => {
		const { color, defaultColor, textColorDisabled } = useSharedState(api);

		if (color === undefined || defaultColor === undefined) {
			return null;
		}
		return (
			<ToolbarCompanyHubTextColor
				pluginState={{
					color,
					defaultColor,
					disabled: textColorDisabled,
				}}
				editorView={editorView}
				popupsMountPoint={popupsMountPoint}
				popupsBoundariesElement={popupsBoundariesElement}
				popupsScrollableElement={popupsScrollableElement}
				isReducedSpacing={isReducedSpacing}
				dispatchAnalyticsEvent={dispatchAnalyticsEvent}
				disabled={disabled}
				pluginInjectionApi={api}
				toolbarType={ToolbarType.PRIMARY}
			/>
		);
	},
);
